import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bus, PartyPopper, Utensils, ImageIcon, MapPin, Check, X } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { AnnouncementAttachmentList } from "@/components/AnnouncementAttachments";
import { GalleryImage } from "@/components/GalleryMedia";
import { guardianCopyFor } from "./guardianCopy";

export default function GuardianServices() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [activeWard, setActiveWard] = useState(user?.wards?.[0]?.id || "");
  const wards = user?.wards || [];

  return (
    <div className="space-y-5" data-testid="guardian-services">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">{t("guardian.servicesTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("guardian.servicesSubtitle")}</p>
      </div>

      {wards.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {wards.map((w) => (
            <button
              key={w.id}
              onClick={() => setActiveWard(w.id)}
              data-testid={`ward-tab-${w.id}`}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${activeWard === w.id ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-white text-[var(--text-secondary)] border-[var(--subtle)]"}`}
            >
              {w.first_name}
            </button>
          ))}
        </div>
      )}

      <Tabs defaultValue="bus" className="w-full min-w-0 max-w-full">
        <TabsList className="w-full max-w-full h-auto overflow-x-auto">
          <TabsTrigger value="bus" className="flex-1 min-w-0 px-1.5 text-xs gap-1" data-testid="tab-bus"><Bus className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{t("guardian.bus")}</span></TabsTrigger>
          <TabsTrigger value="events" className="flex-1 min-w-0 px-1.5 text-xs gap-1" data-testid="tab-events"><PartyPopper className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{t("guardian.events")}</span></TabsTrigger>
          <TabsTrigger value="meals" className="flex-1 min-w-0 px-1.5 text-xs gap-1" data-testid="tab-meals"><Utensils className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{t("guardian.meals")}</span></TabsTrigger>
          <TabsTrigger value="gallery" className="flex-1 min-w-0 px-1.5 text-xs gap-1" data-testid="tab-gallery"><ImageIcon className="h-3.5 w-3.5 flex-shrink-0" /> <span className="truncate">{t("nursery.tabGallery")}</span></TabsTrigger>
        </TabsList>
        <TabsContent value="bus"><BusTab studentId={activeWard} /></TabsContent>
        <TabsContent value="events"><EventsTab studentId={activeWard} /></TabsContent>
        <TabsContent value="meals"><MealsTab /></TabsContent>
        <TabsContent value="gallery"><GalleryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function BusTab({ studentId }) {
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const { data, isLoading } = useQuery({
    queryKey: ["transport", studentId],
    queryFn: async () => unwrap(await api.get(`/students/${studentId}/transport`)),
    enabled: !!studentId,
  });
  if (isLoading) return <Skeleton className="h-96 mt-4" />;
  if (!data) return (
    <Card className="border-[var(--subtle)] mt-4"><CardContent className="py-10 text-center text-[var(--text-muted)]">
      <Bus className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noBusAssigned")}
    </CardContent></Card>
  );
  const { assignment, route, bus } = data;
  return (
    <div className="mt-4 space-y-4">
      <Card className="border-[var(--subtle)] bg-gradient-to-br from-[var(--brand)] to-[var(--brand-hover)] text-white">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{t("guardian.assignedRoute")}</div>
              <div className="font-display text-xl font-bold mt-1">{route?.name}</div>
              <div className="text-sm text-white/80 mt-1 font-mono-num">{bus?.registration_no}</div>
            </div>
            {bus?.last_position && (
              <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                <MapPin className="h-3 w-3 mr-1" /> {copy.live} · {bus.last_position.speed_kmh?.toFixed(0)} km/h
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded bg-white/10 p-3 backdrop-blur-sm">
              <div className="text-[10px] uppercase tracking-widest text-white/60">{t("guardian.driver")}</div>
              <div className="text-sm font-semibold mt-0.5">{bus?.driver_name || "—"}</div>
              <div className="text-xs font-mono-num text-white/70">{bus?.driver_phone}</div>
            </div>
            <div className="rounded bg-white/10 p-3 backdrop-blur-sm">
              <div className="text-[10px] uppercase tracking-widest text-white/60">{t("guardian.conductor")}</div>
              <div className="text-sm font-semibold mt-0.5">{bus?.conductor_name || "—"}</div>
              <div className="text-xs font-mono-num text-white/70">{bus?.conductor_phone}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">{t("guardian.routeStops")}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="p-3 space-y-3">
            {(route?.stops || []).map((s, i) => {
              const isPickup = i === assignment?.pickup_stop_index;
              const isDrop = i === assignment?.drop_stop_index;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 font-mono-num text-xs font-bold ${isPickup || isDrop ? "bg-[var(--accent)] text-white" : "bg-[var(--subtle)] text-[var(--text-secondary)]"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{s.name}</div>
                    <div className="text-xs text-[var(--text-muted)] font-mono-num">{t("guardian.pickupDropAt", { pickup: s.pickup_time, drop: s.drop_time })}</div>
                  </div>
                  {isPickup && <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0">{t("guardian.pickup")}</Badge>}
                  {isDrop && !isPickup && <Badge className="bg-[var(--info-bg)] text-[var(--info)] border-0">{t("guardian.drop")}</Badge>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EventsTab({ studentId }) {
  const { t, language } = useI18n();
  const copy = language === "ar"
    ? { loadError: "تعذر تحميل الفعاليات.", noWard: "اختر طفلاً لإرسال التأكيد.", retry: "إعادة المحاولة" }
    : { loadError: "Unable to load events.", noWard: "Choose a child before sending an RSVP.", retry: "Retry" };
  const formatEventDateRange = (startDate, endDate) => {
    const from = startDate || endDate;
    const to = endDate || startDate;
    if (!from) {
      return "";
    }
    if (from === to) {
      return formatDate(from, language);
    }
    return `${formatDate(from, language)} - ${formatDate(to, language)}`;
  };
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["events-guardian"], queryFn: async () => unwrap(await api.get("/events")) });
  const rsvpMut = useMutation({
    mutationFn: async ({ id, status }) => unwrap(await api.post(`/events/${id}/rsvp`, { status, attendee_count: 2, ...(studentId ? { student_id: studentId } : {}) })),
    onSuccess: () => { toast.success(t("guardian.rsvpSaved")); refetch(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading) return <Skeleton className="h-96 mt-4" />;
  if (isError) return (
    <Card className="border-[var(--subtle)] mt-4"><CardContent className="py-10 text-center text-[var(--text-muted)]">
      <PartyPopper className="h-8 w-8 mx-auto mb-2 opacity-40" />
      <p>{copy.loadError}</p>
      <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">{copy.retry}</Button>
    </CardContent></Card>
  );
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mt-4 space-y-3 stagger">
      {(data || []).map((e) => {
        const eventStartDate = e.start_date || e.event_date;
        const eventEndDate = e.end_date || eventStartDate;
        const past = !!eventEndDate && eventEndDate < today;
        const my = e.my_rsvp;
        return (
          <Card
            key={e.id}
            className={`border-[var(--subtle)] border-t-4 bg-[var(--success-bg)] lift-card ${past ? "opacity-60" : ""}`}
            style={{ borderTopColor: "var(--success)" }}
            data-testid={`event-${e.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-[var(--success-bg)] text-[var(--success)] flex items-center justify-center flex-shrink-0">
                  <PartyPopper className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--text-primary)]">{e.name}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">{formatEventDateRange(eventStartDate, eventEndDate)} · {e.venue}</div>
                  {e.description && <div className="text-xs text-[var(--text-secondary)] mt-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: e.description }} />}
                  <AnnouncementAttachmentList record={e} />
                  {e.allow_rsvp && !past && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => rsvpMut.mutate({ id: e.id, status: "yes" })} disabled={!studentId || rsvpMut.isPending} className={`h-8 ${my?.status === "yes" ? "bg-[var(--success)] text-white hover:bg-emerald-700" : "bg-white border border-[var(--subtle)] text-[var(--text-secondary)] hover:border-[var(--success)]"}`} data-testid={`rsvp-yes-${e.id}`}>
                        <Check className="h-3 w-3 mr-1" /> {t("guardian.yes")}
                      </Button>
                      <Button size="sm" onClick={() => rsvpMut.mutate({ id: e.id, status: "no" })} disabled={!studentId || rsvpMut.isPending} className={`h-8 ${my?.status === "no" ? "bg-[var(--error)] text-white hover:bg-red-700" : "bg-white border border-[var(--subtle)] text-[var(--text-secondary)] hover:border-[var(--error)]"}`} data-testid={`rsvp-no-${e.id}`}>
                        <X className="h-3 w-3 mr-1" /> {t("guardian.no")}
                      </Button>
                    </div>
                  )}
                  {!studentId && e.allow_rsvp && !past && <p className="text-xs text-[var(--error)] mt-2">{copy.noWard}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {(data || []).length === 0 && (
        <Card className="border-[var(--subtle)]"><CardContent className="py-10 text-center text-[var(--text-muted)]">
          <PartyPopper className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noUpcomingEvents")}
        </CardContent></Card>
      )}
    </div>
  );
}

function MealsTab() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["meals-guardian"], queryFn: async () => unwrap(await api.get("/meal-menus")) });
  if (isLoading) return <Skeleton className="h-96 mt-4" />;
  const menu = (data || [])[0];
  if (!menu) return (
    <Card className="border-[var(--subtle)] mt-4"><CardContent className="py-10 text-center text-[var(--text-muted)]">
      <Utensils className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noMenuPublished")}
    </CardContent></Card>
  );
  return (
    <div className="mt-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">{t("guardian.weekOf", { date: formatDate(menu.week_start) })}</CardTitle></CardHeader>
        <CardContent className="space-y-3 p-4">
          {(menu.days || []).map((d) => (
            <div key={d.day_of_week} className="border-l-2 border-[var(--accent)] pl-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">{t(`guardian.day${d.day_of_week}`)}</div>
              <div className="text-sm mt-1"><span className="font-semibold text-[var(--brand)]">{t("guardian.breakfast")}</span> {d.breakfast || "—"}</div>
              <div className="text-sm"><span className="font-semibold text-[var(--brand)]">{t("guardian.lunch")}</span> {d.lunch || "—"}</div>
              {d.snacks && <div className="text-sm"><span className="font-semibold text-[var(--brand)]">{t("guardian.snacks")}</span> {d.snacks}</div>}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function GalleryTab() {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["gallery-parent"],
    queryFn: async () => unwrap(await api.get("/gallery/parent")),
  });
  const { data: consents, isLoading: consentsLoading, isError: consentsError, error: consentsErrorDetail } = useQuery({
    queryKey: ["gallery-consent"],
    queryFn: async () => unwrap(await api.get("/gallery/consent")),
  });
  const consentMutation = useMutation({
    mutationFn: async ({ studentId, canViewPhotos }) => unwrap(await api.patch("/gallery/consent", {
      student_id: studentId,
      can_view_photos: canViewPhotos,
    })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery-parent"] });
      queryClient.invalidateQueries({ queryKey: ["gallery-consent"] });
    },
    onError: (mutationError) => toast.error(errMsg(mutationError)),
  });

  if (isLoading) return <Skeleton className="h-96 mt-4" />;
  if (isError) return (
    <Card className="border-[var(--subtle)] mt-4">
      <CardContent className="py-10 text-center text-sm text-[var(--error)]">{errMsg(error)}</CardContent>
    </Card>
  );
  return (
    <div className="mt-4 space-y-4 stagger" data-testid="guardian-gallery">
      {consentsLoading && (
        <Card className="border-[var(--subtle)]" data-testid="gallery-consent-loading">
          <CardContent className="p-4"><Skeleton className="h-10 w-full" /></CardContent>
        </Card>
      )}
      {consentsError && (
        <Card className="border-[var(--error)]/30" data-testid="gallery-consent-error">
          <CardContent className="p-4 text-sm text-[var(--error)]" role="alert">{errMsg(consentsErrorDetail)}</CardContent>
        </Card>
      )}
      {!consentsLoading && !consentsError && !!consents?.length && (
        <Card className="border-[var(--subtle)]" data-testid="gallery-consent-settings">
          <CardContent className="p-4 space-y-3">
            {consents.map((consent) => {
              const name = `${consent.first_name || ""} ${consent.last_name || ""}`.trim() || consent.student_id;
              return (
                <div key={consent.student_id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[var(--text-primary)] truncate">{name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{t("nursery.tabGallery")}</div>
                  </div>
                  <Switch
                    checked={consent.can_view_photos !== false}
                    disabled={consentMutation.isPending}
                    onCheckedChange={(checked) => consentMutation.mutate({ studentId: consent.student_id, canViewPhotos: checked })}
                    aria-label={`${name} ${t("nursery.tabGallery")}`}
                    data-testid={`gallery-consent-${consent.student_id}`}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      {!data?.length ? (
        <Card className="border-[var(--subtle)]">
          <CardContent className="py-10 text-center text-[var(--text-muted)]">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("nursery.noAlbumsYet")}
          </CardContent>
        </Card>
      ) : data.map((album) => (
        <Card key={album.id} className="border-[var(--subtle)]" data-testid={`parent-album-${album.id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-sm font-display truncate">{album.title}</CardTitle>
                {album.description && <p className="text-xs text-[var(--text-muted)] mt-1">{album.description}</p>}
                {album.album_date && (
                  <p className="text-xs text-[var(--text-muted)] mt-1 font-mono-num">
                    {formatDate(album.album_date, language)}
                  </p>
                )}
              </div>
              <Badge className="flex-shrink-0 bg-[var(--subtle-bg)] text-[var(--text-secondary)] border-0">
                {t("nursery.mediaCount", { count: album.media_count || 0 })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {(album.media || []).map((media) => (
              <div key={media.id} className="overflow-hidden rounded-md border border-[var(--subtle)] bg-[var(--bg)]" data-testid={`parent-media-${media.id}`}>
                {media.kind === "video" ? (
                  <video controls preload="metadata" className="aspect-video w-full bg-black object-cover">
                    <source src={media.url} />
                  </video>
                ) : (
                  <GalleryImage
                    media={media}
                    alt={media.caption || album.title}
                    errorLabel={t("finance.downloadFailed")}
                    className="aspect-video w-full object-cover"
                  />
                )}
                {media.caption && <div className="p-2 text-xs text-[var(--text-secondary)]">{media.caption}</div>}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
